import React, {useEffect, useMemo, useState} from "react";
import {T} from "@tolgee/react";
import {isEmpty} from "lodash";
import {Box, Button, Paper, Typography} from "@material-ui/core";
import {useForm} from "react-hook-form";
import {
  FullProduct,
  LocalStorageProduct,
  ProductNoteAttachment,
  RawNotes,
} from "ts/api/product";
import {generatePath, Link} from "react-router-dom";
import {SHOW_EXHIBITOR_SHOPPING_PATH} from "view/routes";
import Form from "view/components/Form";
import RightArrowIcon from "view/images/RightArrowIcon";
import {useCurrentShowId} from "hooks";
import {getItem} from "tools/localStorage";
import {updateNotesAttachments} from "api/order";
import Products, {
  ShoppingCartEventProps,
} from "view/components/ShoppingCart/Products";
import useCurrentExhibitorPairId from "hooks/useCurrentExhibitorPairId";
import {generateTestId} from "tools/cypress/generateTestId";
import {useQuickOrderId} from "hooks/show/order";
import {useIsShowDiscountCodesInvalid} from "store/discount-code/discount-code-store";
import useBranding from "store/branding/branding.store";
import {BrandingType} from "@evennt-tech/api-v2-core";
import {useProductPurchasesPerStand} from "store/product/product-purchase-per-stand.store";
import useCurrentExhibitorPair from "hooks/useCurrentExhibitorPair";
import ProductAttachmentsModal from "./components/ProductAttachmentsModal";
import useStyles from "./styles";
import {isNotAvailableForPurchase} from "./components/ProductItem";

export type ShoppingCartProps = ShoppingCartEventProps & {
  onCheckout: (notes: RawNotes) => Promise<void>;
  onProductAttachmentsChange: (
    id: string,
    attachments: ProductNoteAttachment[]
  ) => void;
  productsWithApproval: FullProduct[];
  productsWithoutApproval: FullProduct[];
};

const ShoppingCart: React.FC<ShoppingCartProps> = ({
  onCheckout,
  onNoteChange,
  onRemoveProduct,
  onRemoveExtraProduct,
  onChangeQuantity,
  onChangeExtraProductQuantity,
  onProductAttachmentsChange,
  productsWithApproval,
  productsWithoutApproval,
  linkedPropertyByProductId,
}) => {
  const quickOrderId = useQuickOrderId();
  const classes = useStyles();
  const showId = useCurrentShowId();
  const exhibitorPairId = useCurrentExhibitorPairId();
  const [attachmentModal, setAttachmentModal] = useState<{
    productId: string;
    firstAttachment?: ProductNoteAttachment;
  }>();
  const isDiscountCodesInvalid = useIsShowDiscountCodesInvalid(showId);
  const [exhibitorPair] = useCurrentExhibitorPair({showId, exhibitorPairId});

  const purchaseCountByProduct = useProductPurchasesPerStand((state) => {
    if (!exhibitorPair) return {};
    return state.items.get(exhibitorPair.standId) ?? {};
  });

  const combinedProducts = useMemo(
    () => productsWithApproval.concat(productsWithoutApproval),
    [productsWithApproval, productsWithoutApproval]
  );

  const defaultValues = useMemo(
    () =>
      combinedProducts.reduce(
        (acc, product) => ({
          ...acc,
          [product.id]: {
            note: product?.notes?.note,
            attachments: product?.notes?.attachments,
          },
        }),
        {}
      ),
    [combinedProducts]
  );

  const form = useForm<RawNotes>({
    mode: "all",
    defaultValues,
    shouldFocusError: true,
  });
  const {watch, register, reset} = form;

  useEffect(() => {
    combinedProducts.forEach((product) => {
      register(`${product.id}.note`);
      register(`${product.id}.attachments`);
    });
  }, [combinedProducts, register]);

  useEffect(() => {
    reset(defaultValues);
  }, [reset, defaultValues]);

  const formValues = watch();

  const onAttachmentModalOpen = (
    productId: string,
    firstAttachment?: ProductNoteAttachment
  ) => {
    setAttachmentModal({productId, firstAttachment});
  };

  const onAttachmentModalClose = () => {
    setAttachmentModal(undefined);
  };
  const onSubmitProductAttachment = async (
    productId: string,
    newAttachments: ProductNoteAttachment[]
  ) => {
    const localStorageProducts = getItem<LocalStorageProduct[]>("products", []);
    const updatingProduct = localStorageProducts?.find(
      ({id}) => id === productId
    );
    const uploadedAttachments = await updateNotesAttachments({
      productId,
      newAttachments,
      oldAttachments: updatingProduct?.notes?.attachments || [],
    });

    onProductAttachmentsChange(productId, uploadedAttachments);

    return uploadedAttachments;
  };

  const onAttachmentModalSubmit = async (
    attachments: ProductNoteAttachment[]
  ) => {
    if (attachmentModal) {
      await onSubmitProductAttachment(attachmentModal.productId, attachments);

      onAttachmentModalClose();
    }
  };

  const getAttachments = (): ProductNoteAttachment[] => {
    if (!attachmentModal) {
      return [];
    }

    if (attachmentModal.firstAttachment) {
      return [attachmentModal.firstAttachment];
    }

    return formValues[attachmentModal.productId].attachments;
  };

  const someIsNotAvailableForPurchase = () => {
    if (quickOrderId) {
      return false;
    }

    return combinedProducts.some((product) => {
      const productPurchaseCount = purchaseCountByProduct[product.id] ?? 0;
      return isNotAvailableForPurchase(product, productPurchaseCount);
    });
  };

  const productEvents = {
    onChangeExtraProductQuantity,
    onChangeQuantity,
    onRemoveProduct,
    onAttachmentModalOpen,
    onNoteChange,
    onRemoveExtraProduct,
  } as const;

  const {brandings} = useBranding();
  const branding = brandings[showId];

  const isImageOnBackground = branding?.backgroundType === BrandingType.IMAGE;

  return (
    <Paper elevation={0} variant="outlined" className={classes.cart}>
      <Box pl={3} pr={3} pb={3}>
        <Typography variant="h2">
          <T keyName="common.shopping_cart.title" />
        </Typography>
      </Box>
      <Form
        className={classes.form}
        style={{
          padding: 0,
          backgroundColor: "transparent",
          maxHeight: isImageOnBackground ? "500px" : "unset",
          overflowY: isImageOnBackground ? "auto" : "unset",
        }}
        form={form}
        onSubmit={() => null}
      >
        <Box marginTop={3} marginBottom={3}>
          <Products
            products={productsWithoutApproval}
            {...productEvents}
            linkedPropertyByProductId={linkedPropertyByProductId}
          />
          <Products
            products={productsWithApproval}
            {...productEvents}
            linkedPropertyByProductId={linkedPropertyByProductId}
          />
        </Box>
      </Form>
      <Box display="flex" justifyContent="space-between" pl={3} pr={3} pt={3}>
        {!quickOrderId && (
          <Button
            {...generateTestId("exhibitor-cart-continue-shopping-btn")}
            component={Link}
            color="primary"
            to={generatePath(SHOW_EXHIBITOR_SHOPPING_PATH, {
              showId,
              exhibitorPairId,
            })}
          >
            <T keyName="common.shopping_cart.continue_shopping" />
          </Button>
        )}
        {!!quickOrderId && <div />}
        <Button
          {...generateTestId("exhibitor-cart-checkout-btn")}
          variant="contained"
          color="primary"
          disabled={
            isEmpty(combinedProducts) ||
            someIsNotAvailableForPurchase() ||
            isDiscountCodesInvalid
          }
          onClick={form.handleSubmit((values) => onCheckout(values))}
          endIcon={<RightArrowIcon color="primary" />}
        >
          <T
            keyName={
              quickOrderId ? "common.action.next" : "common.action.checkout"
            }
          />
        </Button>
      </Box>
      {attachmentModal && (
        <ProductAttachmentsModal
          isOpen
          onClose={onAttachmentModalClose}
          onSubmit={onAttachmentModalSubmit}
          isFirstAttachment={!!attachmentModal.firstAttachment}
          attachments={getAttachments()}
        />
      )}
    </Paper>
  );
};

export default ShoppingCart;
